import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { FormGroup } from '@/components/FormGroup';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { useAuth } from '@/contexts/AuthContext';
import { useUpdateVtexCredentials } from '@/hooks/useUpdateVtexCredentials';
import i18n from '@/lib/i18n';

const formSchema = z.object({
  url: z
    .string()
    .min(1, {
      message: i18n.t(
        'pages.Settings.components.Vtex.components.Form.validation.urlRequired',
      ),
    })
    .url(
      i18n.t(
        'pages.Settings.components.Vtex.components.Form.validation.urlInvalid',
      ),
    ),
  key: z.string().min(1, {
    message: i18n.t(
      'pages.Settings.components.Vtex.components.Form.validation.keyRequired',
    ),
  }),
  token: z.string().min(1, {
    message: i18n.t(
      'pages.Settings.components.Vtex.components.Form.validation.tokenRequired',
    ),
  }),
  salesChannel: z.string().min(1, {
    message: i18n.t(
      'pages.Settings.components.Vtex.components.Form.validation.salesChannelRequired',
    ),
  }),
  affiliateId: z.string().min(1, {
    message: i18n.t(
      'pages.Settings.components.Vtex.components.Form.validation.affiliateIdRequired',
    ),
  }),
});

type FormData = z.infer<typeof formSchema>;

export function Form() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Settings.components.Vtex.components.Form',
  });

  const { user } = useAuth();

  const [isEditable, setIsEditable] = useState<boolean>(() => {
    return !(
      user?.customer.vtexUrl &&
      user?.customer.vtexAppKey &&
      user?.customer.vtexAppToken &&
      user?.customer.vtexSalesChannel &&
      user?.customer.vtexAffiliateId
    );
  });

  const {
    formState: { isSubmitting, errors },
    register,
    handleSubmit,
    setValue,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      url: user?.customer.vtexUrl ?? '',
      key: user?.customer.vtexAppKey ?? '',
      token: user?.customer.vtexAppToken ?? '',
      salesChannel: String(user?.customer.vtexSalesChannel ?? 1),
      affiliateId: user?.customer.vtexAffiliateId ?? 'FFF',
    },
  });

  const { updateVtexCredentials } = useUpdateVtexCredentials();

  function handleEdit() {
    setValue('key', '');
    setValue('token', '');
    setIsEditable(true);
  }

  async function onSubmit({
    url,
    key,
    token,
    salesChannel,
    affiliateId,
  }: FormData) {
    try {
      await updateVtexCredentials({
        vtexUrl: url,
        vtexAppKey: key,
        vtexAppToken: token,
        vtexSalesChannel: Number(salesChannel),
        vtexAffiliateId: affiliateId,
      });

      toast.success(t('toast.success'));
      setIsEditable(false);
    } catch {
      toast.error(t('toast.error'));
    }
  }

  return (
    <form
      className="flex h-full flex-col space-y-6"
      noValidate
      onSubmit={handleSubmit(onSubmit)}
    >
      <FormGroup
        className="space-y-2"
        error={{ message: errors.url?.message, font: 'sm' }}
      >
        <Label htmlFor="url">{t('storeUrlLabel')}</Label>

        <Input
          id="url"
          placeholder={t('storeUrlPlaceholder')}
          className="w-full"
          disabled={!isEditable}
          {...register('url')}
        />

        {!errors.url?.message && (
          <p
            className="text-sm text-muted-foreground"
            dangerouslySetInnerHTML={{
              __html: t('storeUrlError'),
            }}
          />
        )}
      </FormGroup>

      <FormGroup
        className="space-y-2"
        error={{ message: errors.key?.message, font: 'sm' }}
      >
        <Label htmlFor="vtexApiKey">{t('apiKeyLabel')}</Label>

        <Input
          id="vtexApiKey"
          type="password"
          placeholder={t('apiKeyPlaceholder')}
          className="w-full"
          disabled={!isEditable}
          {...register('key')}
        />

        {!errors.key?.message && (
          <p className="text-sm text-muted-foreground">{t('apiKeyError')}</p>
        )}
      </FormGroup>

      <FormGroup
        className="space-y-2"
        error={{ message: errors.token?.message, font: 'sm' }}
      >
        <Label htmlFor="vtexApiToken">{t('apiTokenLabel')}</Label>

        <Input
          id="vtexApiToken"
          type="password"
          placeholder={t('apiTokenPlaceholder')}
          className="w-full"
          disabled={!isEditable}
          {...register('token')}
        />

        {!errors.token?.message && (
          <p className="text-sm text-muted-foreground">{t('apiTokenError')}</p>
        )}
      </FormGroup>

      <FormGroup
        className="space-y-2"
        error={{ message: errors.salesChannel?.message, font: 'sm' }}
      >
        <Label htmlFor="vtexSalesChannel">{t('salesChannelLabel')}</Label>

        <Input
          id="vtexSalesChannel"
          type="number"
          placeholder={t('salesChannelPlaceholder')}
          className="w-full"
          disabled={!isEditable}
          {...register('salesChannel')}
        />

        {!errors.salesChannel?.message && (
          <p className="text-sm text-muted-foreground">
            {t('salesChannelError')}
          </p>
        )}
      </FormGroup>

      <FormGroup
        className="space-y-2"
        error={{ message: errors.affiliateId?.message, font: 'sm' }}
      >
        <Label htmlFor="vtexaffiliateId">{t('affiliateIdLabel')}</Label>

        <Input
          id="vtexaffiliateId"
          placeholder={t('affiliateIdPlaceholder')}
          className="w-full"
          disabled={!isEditable}
          {...register('affiliateId')}
        />

        {!errors.affiliateId?.message && (
          <p className="text-sm text-muted-foreground">
            {t('affiliateIdError')}
          </p>
        )}
      </FormGroup>

      {isEditable && (
        <Button
          type="submit"
          className="mt-auto w-full bg-black text-white transition-colors hover:bg-gray-800"
          disabled={isSubmitting}
        >
          {isSubmitting ? t('savingButton') : t('saveButton')}
        </Button>
      )}

      {!isEditable && (
        <Button
          type="button"
          className="mt-auto w-full bg-black text-white transition-colors hover:bg-gray-800"
          onClick={handleEdit}
        >
          {t('editButton')}
        </Button>
      )}
    </form>
  );
}
